<div class="space-y-8 mx-auto max-w-6xl p-4 md:p-10 min-h-screen">
    <h2 class="text-xl md:text-2xl font-bold text-gray-900 border-b pb-4">
      {{ 'Shipping Address' | translate }}
    </h2>

    @if (errorMessage) {
      <div
        role="alert"
        class="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2 transition-all duration-300"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 shrink-0 text-red-500"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fill-rule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
            clip-rule="evenodd"
          />
        </svg>
        <span>{{ errorMessage }}</span>
</div>
    }

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
      <div class="flex flex-col gap-2">
        <label for="fullName" class="text-sm font-normal text-[#0D3543]">{{
          'Name' | translate
        }}</label>
        <div class="relative">
          <sham-i-user
            class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 size-5"
          />
          <input
            type="text"
            id="fullName"
            [(ngModel)]="addressForm.fullName"
            [placeholder]="'Full Name' | translate"
            class="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-sky-100 outline-none transition"
            [class.border-red-400]="showFullNameError()"
            [class.border-[#BBDEEB]]="!showFullNameError()"
          />
        </div>
        @if (showFullNameError()) {
          <p class="text-sm text-red-600">{{ getFullNameErrorMessage() }}</p>
        }
      </div>

      <div class="flex flex-col gap-2">
        <label for="streetAddress" class="text-sm font-normal text-[#0D3543]">
          {{ 'Street Address' | translate }}
        </label>
        <div class="relative">
          <sham-i-location-address
            class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 size-5"
          />
          <input
            type="text"
            id="streetAddress"
            [(ngModel)]="addressForm.streetAddress"
            [placeholder]="'Street Address' | translate"
            class="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-sky-100 outline-none transition"
            [class.border-red-400]="showStreetAddressError()"
            [class.border-[#BBDEEB]]="!showStreetAddressError()"
          />
        </div>
        @if (showStreetAddressError()) {
          <p class="text-sm text-red-600">{{ getStreetAddressErrorMessage() }}</p>
        }
      </div>

      <div class="flex flex-col gap-2">
        <label for="countryId" class="text-sm font-normal text-[#0D3543]">
          {{ 'Country / Region' | translate }}
        </label>
        <div class="relative">
          <sham-i-location-address
            class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 size-5"
          />
          <select
            id="countryId"
            [(ngModel)]="addressForm.countryId"
            (change)="onCountryChange()"
            class="w-full pl-10 pr-4 py-3 border rounded-lg appearance-none focus:ring-2 focus:ring-sky-100 outline-none transition cursor-pointer"
            [class.border-red-400]="showCountryError()"
            [class.border-[#BBDEEB]]="!showCountryError()"
          >
            <option value="">{{ 'Country / Region' | translate }}</option>
            @for (country of countries; track country.id) {
              <option [ngValue]="country.id">{{ country.name }}</option>
            }
          </select>
        </div>
        @if (showCountryError()) {
          <p class="text-sm text-red-600">{{ getCountryErrorMessage() }}</p>
        }
      </div>

      <div class="flex flex-col gap-2">
        <label for="cityId" class="text-sm font-normal text-[#0D3543]">{{
          'City' | translate
        }}</label>
        <div class="relative">
          <sham-i-location-address
            class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 size-5"
          />
          <select
            id="cityId"
            [(ngModel)]="addressForm.cityId"
            (change)="onCityChange()"
            class="w-full pl-10 pr-4 py-3 border rounded-lg appearance-none focus:ring-2 focus:ring-sky-100 outline-none transition cursor-pointer"
            [class.border-red-400]="showCityError()"
            [class.border-[#BBDEEB]]="!showCityError()"
          >
            <option value="">{{ 'City' | translate }}</option>
            @for (city of cities; track city.id) {
              <option [ngValue]="city.id">{{ city.name }}</option>
            }
          </select>
        </div>
        @if (showCityError()) {
          <p class="text-sm text-red-600">{{ getCityErrorMessage() }}</p>
        }
      </div>

      <div class="flex flex-col gap-2">
        <label for="email" class="text-sm font-normal text-[#0D3543]">{{
          'Email' | translate
        }}</label>
        <div class="relative">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 size-5"
          >
            <rect width="20" height="16" x="2" y="4" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
          <input
            type="email"
            id="email"
            [(ngModel)]="addressForm.email"
            [placeholder]="'Email' | translate"
            class="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-sky-100 outline-none transition"
            [class.border-red-400]="showEmailError()"
            [class.border-[#BBDEEB]]="!showEmailError()"
          />
        </div>
        @if (showEmailError()) {
          <p class="text-sm text-red-600">{{ getEmailErrorMessage() }}</p>
        }
      </div>

      <div class="flex flex-col gap-2">
        <label for="phone" class="text-sm font-normal text-[#0D3543]">
          {{ 'Phone Number' | translate }}
        </label>
        <div class="relative">
          <sham-i-phone-03
            class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 size-5"
          />
          <input
            type="text"
            id="phone"
            [(ngModel)]="addressForm.phone"
            [placeholder]="'Phone Number' | translate"
            class="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-sky-100 outline-none transition"
            [class.border-red-400]="showPhoneError()"
            [class.border-[#BBDEEB]]="!showPhoneError()"
          />
        </div>
        @if (showPhoneError()) {
          <p class="text-sm text-red-600">{{ getPhoneErrorMessage() }}</p>
        }
      </div>
    </div>

    <div class="flex items-center gap-2">
      <input
        type="checkbox"
        [(ngModel)]="addressForm.isDefault"
        id="makeDefault"
        class="w-5 h-5 rounded border-gray-300 accent-[#2596BE] cursor-pointer"
      />
      <label
        for="makeDefault"
        class="text-sm font-medium text-gray-700 cursor-pointer"
      >
        {{ 'Make This Address Default' | translate }}
      </label>
    </div>

    <app-location-picker
      [latitude]="addressForm.latitude"
      [longitude]="addressForm.longitude"
      [isPinConfirmed]="addressForm.isPinConfirmed"
      [viewport]="mapViewport"
      (locationChange)="onLocationChange($event)"
    />
    @if (showLocationError()) {
      <p class="text-sm text-red-600" role="alert">
        {{ 'addressValidation.locationRequired' | translate }}
      </p>
    }

    <div class="flex flex-col gap-4 py-6">
      <button
        type="button"
        (click)="saveAddress()"
        [disabled]="isSaving"
        class="w-full bg-[#2596BE] hover:bg-[#1a7a9c] disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-3.5 rounded-lg font-bold transition shadow-md flex items-center justify-center gap-2"
      >
        <svg
          class="animate-spin h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          [class.hidden]="!isSaving"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <span>{{ (isSaving ? 'Loading...' : 'Save') | translate }}</span>
      </button>
      <button
        (click)="goBack()"
        type="button"
        class="w-fit flex items-center gap-2 px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition font-medium"
      >
        <span class="text-lg">&lt;</span> {{ 'Previous' | translate }}
      </button>
    </div>
  </div>
